import { type ComponentType, lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { PageLoader } from 'routes/PrivateRouter';
import {
  closeOverviewTabsMap,
  closeOverviewURLs,
} from 'routes/close/closeURLs';

import { PermissionGuard } from 'components/PermissionGuard';

import { useCustomerFeatureFlags } from 'hooks/useCustomerFlags';
import { useIsMaximorUser } from 'hooks/useIsMaximorUser';

import { featureFlags } from 'utils/config/featureFlags';

import type { CloseOverviewTabs } from 'types/close/CloseOverviewCommon';

interface OverviewProps {
  tab: CloseOverviewTabs;
}

const CloseOverview = lazy<ComponentType<OverviewProps>>(() =>
  import('pages/period-close/close-overview/CloseOverview').then((m) => ({
    default: m.CloseOverview,
  }))
);

const CloseOverviewDemo = lazy<ComponentType<OverviewProps>>(() =>
  import('pages/period-close/demo/CloseOverviewDemo').then((m) => ({
    default: m.CloseOverviewDemo,
  }))
);

const OverviewComponent = featureFlags.FF__CLOSE_MONTH_CLOSE
  ? CloseOverviewDemo
  : CloseOverview;

const CloseOverviewElement = ({ tab }: { tab: CloseOverviewTabs }) => {
  const isMaximorUser = useIsMaximorUser();
  const {
    FF__IS_FACILIO_CUSTOMER,
    FF__IS_RENTLY_CUSTOMER,
    FF__IS_INVST_CUSTOMER,
  } = useCustomerFeatureFlags();
  const isTargetCustomer =
    FF__IS_FACILIO_CUSTOMER || FF__IS_RENTLY_CUSTOMER || FF__IS_INVST_CUSTOMER;

  const content = (
    <Suspense fallback={<PageLoader />}>
      <OverviewComponent tab={tab} />
    </Suspense>
  );

  // Maximor users on target customer workspaces bypass the permission guard
  // so they can access close insights regardless of module permissions
  if (isMaximorUser && isTargetCustomer) return content;

  return (
    <PermissionGuard moduleKey="close-overview">{content}</PermissionGuard>
  );
};

export const CloseOverviewRoutes = {
  path: closeOverviewURLs._baseRelativePath,
  children: [
    {
      index: true,
      element: <Navigate to={closeOverviewURLs.insights} replace />,
    },
    ...Object.entries(closeOverviewTabsMap)
      .filter(
        ([tab]) => tab !== 'month-close' || featureFlags.FF__CLOSE_MONTH_CLOSE
      )
      .map(([tab, { relativePath }]) => ({
        path: relativePath,
        element: <CloseOverviewElement tab={tab as CloseOverviewTabs} />,
      })),
  ],
};
